import { Component, Inject, Input, OnInit } from '@angular/core';
import { BaseCustomDialog } from '../base-custom-dialog';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ACTION_DATA_SERVICE, ActionDataService } from '../../../../services/action/data.service';
import { IActionTypes } from '../../../../models/action.model';
import { DateTimeHelper, TimePeriodType } from '../../../../helper/date-time.helper';
import { ActionIconHelper } from '../../../../helper/action-icon.helper';
import { LEADS_DATA_SERVICE, LeadsDataService } from '../../../../services/leads/leads-data.service';
import { AppointmentsHelper } from '../../../../helper/appointments.helper';
import { UserTokenService } from '../../../../services/user-service/user-token.service';
import { ICompanyContacts, IContact, ProfileStudio } from "../../../../models/leads/leads.model";
import { ConvertHelper } from "../../../../helper/convert.helper";
import { PROFILE_DATA_SERVICE, ProfileDataService } from '../../../../services/profile/profile-data.service';
import { LAST_SELECTED_STUDIO_ID } from '../../../../constants/constants';
import { LeadHelper } from '../../../../helper/lead.helper';
import { CurrencyHelper } from '../../../../helper/app-currency';


@Component({
  selector: 'fi-consultation-create-popup',
  templateUrl: './consultation-create-popup.component.html',
  styleUrls: ['./consultation-create-popup.component.scss'],
  standalone: false,
})
export class ConsultationCreatePopupComponent extends BaseCustomDialog implements OnInit {
  @Input() isNewLeadRequest: boolean;

  contactName = '';
  leadId: number;
  selectedDateOptionId: number;
  appointmentTypeId: number;
  showroomAppointment: IActionTypes;
  showroomTypeId: string;

  readonly dateOptionTypes = DateTimeHelper.datePeriodTypes;

  appointmentOptionTypes: IActionTypes[] = [];
  showroomTypes: ProfileStudio[] = [];
  searchedText = '';
  public note = '';
  selectedDate: Date;
  title: string;
  iconUrl = ActionIconHelper.getActionIconUrl('CONSULTATION_CREATE', false);
  darkIconUrl = ActionIconHelper.getActionIconUrl('CONSULTATION_CREATE', true);
  submitting: boolean;
  companyContacts: ICompanyContacts[];
  contactToDisplay: IContact;
  appointmentExists = false;
  leadOfferId = LeadHelper.LEAD_OFFER_ID;
  leadTypeId: number;
  extendOptionalFields = false;
  isExternal = false;
  currencies = CurrencyHelper.FINSTRAL_CURRENCIES;
  public extendedProperties = {
    OFFER_AMOUNT: null,
    OFFER_CURRENCY: ''
  };
  inValidNote = false;

  constructor(
    private userTokenService: UserTokenService,
    public dialogRef: MatDialogRef<ConsultationCreatePopupComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: {
      isExternal?: boolean,
      contactName: string,
      leadId: number,
      leadTypeId: number,
      title: string,
      externalEmail: string
    },
    @Inject(ACTION_DATA_SERVICE) private actionDataService: ActionDataService,
    @Inject(LEADS_DATA_SERVICE) private leadsDataService: LeadsDataService,
    @Inject(PROFILE_DATA_SERVICE) private profileDataService: ProfileDataService,
  ) {
    super(dialogRef);
    this.extendedProperties.OFFER_CURRENCY = this.userTokenService.getCurrency() ?? CurrencyHelper.DEFAULT_CURRENCY;
    this.contactName = dialogData?.contactName || '';
    this.leadId = dialogData?.leadId;
    this.title = dialogData?.title;
    this.leadTypeId = dialogData?.leadTypeId;
    this.isExternal = dialogData?.isExternal;

    if ( this.dialogData?.externalEmail ) {
      this.darkIconUrl = ActionIconHelper.getActionIconUrl('CONSULTATION_CREATE_EXTERNAL', true);
    }

    this.actionDataService.getAppointmentTypes().subscribe(( { success, data } ) => {
      if ( success ) {
        this.appointmentOptionTypes = data.filter(item => item.IsLocation);
        this.showroomAppointment = this.appointmentOptionTypes.find(item => item.Name === 'SCHAURAUM');
      }
    });

    this.profileDataService.getStudios().subscribe(( { success, data } ) => {
      if ( success ) {
        this.showroomTypes = data;
        if (!this.isExternal) {
          this.showroomTypeId = sessionStorage.getItem(LAST_SELECTED_STUDIO_ID) ?? '';
        }
      }
    });

    if ( !!this.leadId ) {
      this.leadsDataService.getLeadById(this.leadId).subscribe(( { success, data } ) => {
        if ( success ) {
          this.contactToDisplay = data.Contact;
          this.companyContacts = data.CompanyContacts;
        }
      });
    }

  }

  onDateSelect( date: Date, time = false ): void {
    this.selectedDateOptionId = null;

    if ( !date ) {
      this.selectedDate = null;
      return;
    }

    if ( !this.selectedDate ) {
      this.selectedDate = date;
    } else if ( !time ) {
      this.selectedDate.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    } else {
      this.selectedDate.setHours(date.getHours(), date.getMinutes());
    }

    this.selectedDate = new Date(this.selectedDate);
    this.appointmentExists = AppointmentsHelper.getAppointments(this.selectedDate, this.appointmentExists);
  }

  setPeriods( $event ): void {
    this.selectedDateOptionId = $event;
    this.selectedDate = DateTimeHelper.getDateAfterPeriod(this.dateOptionTypes.find(item => item.Id === +$event));
    this.appointmentExists = AppointmentsHelper.getAppointments(this.selectedDate, this.appointmentExists);
  }

  onDateOptionSelected( type: TimePeriodType ): void {
    this.selectedDateOptionId = type.Id;
    this.selectedDate = DateTimeHelper.getDateAfterPeriod(type);
    this.appointmentExists = AppointmentsHelper.getAppointments(this.selectedDate, this.appointmentExists);
  }

  async ngOnInit(): Promise<void> {
    const userId = await this.userTokenService.getId();
    AppointmentsHelper.getDbAppointments(this.actionDataService, userId);
  }

  closeDialog(): void {
    this.clearClassAndCloseDialog(this.dialogRef);
  }

  get formIsValid(): boolean {
    const hasRequiredFields = !!this.leadId && !!this.selectedDate && !!this.appointmentTypeId;
    // checks if selected appointment is showroom making that field mandatory
    const isShowroomRequired = !this.isExternal && this.appointmentTypeId === this.showroomAppointment?.Id ? !!this.showroomTypeId : true;
    const isNotSubmitting = !this.submitting;
    const isNoteValid = !this.inValidNote;

    return hasRequiredFields && isShowroomRequired && isNotSubmitting && isNoteValid;
  }

  showRoomText = ( item: ProfileStudio ) => `${item.KKZ} - ${item.Name} - ${item.City}`;

  setExtendedProperties( currency: string ): void {
    this.currencies.forEach(el => {
      if ( el.Id.toString() === currency ) {
        this.extendedProperties.OFFER_CURRENCY = el.Description;
      }
    })
  }

  inValidMaxLength(): boolean {
    this.inValidNote = this.note.length > 2000;
    return this.inValidNote;
  }

  submit(): void {

    if ( this.formIsValid ) {
      this.submitting = true;
      if ( !!this.extendedProperties.OFFER_AMOUNT ) {
        this.extendedProperties.OFFER_AMOUNT = ConvertHelper.currencyToNumber(this.extendedProperties.OFFER_AMOUNT);
      }
      this.actionDataService.createConsultation(
        this.leadId,
        this.note,
        this.appointmentTypeId,
        this.selectedDate,
        this.extendedProperties,
        !this.isExternal && this.appointmentTypeId === this.showroomAppointment?.Id ? this.showroomTypeId : undefined,
        !!this.dialogData.externalEmail,
      )
        .subscribe(( { success, data } ) => {
          if ( success ) {
            this.clearClassAndCloseDialog(this.dialogRef, 'remove');
          }
          if (!this.isExternal && this.appointmentTypeId === this.showroomAppointment?.Id) {
            sessionStorage.setItem(LAST_SELECTED_STUDIO_ID, this.showroomTypeId);
          }
          this.submitting = false;
        });
    }
  }
}
